import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader, Space, Tabs } from 'antd';
import React from 'react';
import { DRAWER_WIDTH } from '../../../config/constants';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { translations } from '../../../config/translations';
import { ActionType, DrawerState, ModuleName } from '../../../core/models/enum';
import { RouteParamsGeneric } from '../../../core/models/RouteParams';
import { authorizeAction } from '../../../helpers/CheckPermissionHelper';
import { showConfirm } from '../../../helpers/NotificationHelper';
import { buildRoute, buildSubPageRoute, getNewRoute } from '../../../helpers/RoutingHelper';
import { remove } from '../../../helpers/SubmitHelper';
import {
    AttachmentsClient,
    AttachmentVm,
    ToolDetailVm,
    ToolsClient,
} from '../../../utils/api';
import { Profile } from 'oidc-client';
import AttachmentForm from '../../attachments/attachment-form';
import AttachmentTable from '../../attachments/attachment-table';
import ToolForm from '../tool-form/ToolForm';
import ToolDetails from './tool-details/ToolDetails';
import { RouteComponentProps, withRouter } from 'react-router-dom';

const { TabPane } = Tabs;

interface Props extends RouteComponentProps<RouteParamsGeneric> {
    userProfile: Profile;
}

interface State {
    tool?: ToolDetailVm;
    drawerState: DrawerState;
    selectedAttachment?: AttachmentVm;
    activeTab: 'details' | 'attachments';
    allowUpdate: boolean;
    allowDelete: boolean;
    allowCreateAttachment: boolean;
}

class ToolPage extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        const { userProfile, match } = props;

        this.state = {
            activeTab: match.params.subPage === 'attachments' ? 'attachments' : 'details',
            allowUpdate: authorizeAction(userProfile, ModuleName.Tools, ActionType.Update),
            allowDelete: authorizeAction(userProfile, ModuleName.Tools, ActionType.Delete),
            allowCreateAttachment: authorizeAction(
                userProfile,
                ModuleName.Attachments,
                ActionType.Create,
                undefined,
                parseInt(match.params.entityId, 10)
            ),
            drawerState: ToolPage.getInitDrawerState(props),
        };
    }

    private static getInitDrawerState(props: Props): DrawerState {
        switch (props.match.path) {
            case Routes.ROUTE_TOOLS_SUBPAGE_NEW:
                return DrawerState.Create;
            case Routes.ROUTE_TOOLS_SUBPAGE_EDIT:
                return DrawerState.Edit;
            case Routes.ROUTE_TOOLS_EDIT:
                return DrawerState.Edit;
            default:
                return DrawerState.Closed;
        }
    }

    public componentDidMount = () => {
        void this.getTool();
    };

    public componentDidUpdate(prevProps: Props): void {
        const { match } = this.props;

        if (prevProps.match.params.entityId !== match.params.entityId) {
            void this.getTool();
            return;
        }

        if (
            prevProps.match.params.subPage !== match.params.subPage ||
            prevProps.match.path !== match.path ||
            prevProps.match.params.subEntityId !== match.params.subEntityId
        ) {
            const activeTab = match.params.subPage === 'attachments' ? 'attachments' : 'details';
            this.setState(
                {
                    activeTab,
                    drawerState: ToolPage.getInitDrawerState(this.props),
                },
                () => {
                    if (this.state.tool) {
                        this.syncAttachmentSelectionFromRoute();
                    }
                }
            );
        }
    }

    private getTool = async (): Promise<void> => {
        const {
            match: {
                params: { entityId },
            },
        } = this.props;

        if (!entityId) return;

        const tool = await new ToolsClient().getById(+entityId);
        this.setState({ tool }, () => this.syncAttachmentSelectionFromRoute());
    };

    private syncAttachmentSelectionFromRoute = (): void => {
        const { match } = this.props;
        const { tool, activeTab } = this.state;

        if (!tool || activeTab !== 'attachments') {
            return;
        }

        if (match.path === Routes.ROUTE_TOOLS_SUBPAGE_NEW) {
            this.setState({ drawerState: DrawerState.Create, selectedAttachment: undefined });
            return;
        }

        if (match.path === Routes.ROUTE_TOOLS_SUBPAGE_EDIT) {
            const subId = match.params.subEntityId ? +match.params.subEntityId : NaN;
            const att = tool.attachments?.find(
                (a) => a.id === subId && !a.isMultimedia
            );
            this.setState({
                drawerState: DrawerState.Edit,
                selectedAttachment: att,
            });
            return;
        }

        this.setState({ drawerState: DrawerState.Closed, selectedAttachment: undefined });
    };

    private handleSuccess = (tool: ToolDetailVm) => {
        this.setState({
            tool,
        });
        this.handleDrawerMode(DrawerState.Closed);
    };

    private handleDrawerMode = (drawerState: DrawerState) => {
        const { history } = this.props;
        const { tool } = this.state;

        if (!tool) return;

        this.setState({
            drawerState,
        });

        history.push(
            getNewRoute(
                Routes.ROUTE_TOOLS_READ,
                Routes.ROUTE_TOOLS_READ,
                Routes.ROUTE_TOOLS_EDIT,
                drawerState,
                ReplaceStrings.ENTITY_ID,
                tool.id.toString()
            )
        );
    };

    private handleDelete = () => {
        const { history } = this.props;

        history.push(Routes.ROUTE_TOOLS);
    };

    private handleTabChange = (activeKey: string) => {
        const { history } = this.props;
        const { tool } = this.state;

        const key = activeKey === 'attachments' ? 'attachments' : 'details';

        this.setState({
            activeTab: key,
            drawerState: DrawerState.Closed,
            selectedAttachment: undefined,
        });

        if (!tool) {
            void this.getTool();
            return;
        }

        if (key === 'details') {
            history.push(
                buildRoute(
                    Routes.ROUTE_TOOLS,
                    Routes.ROUTE_TOOLS_NEW,
                    Routes.ROUTE_TOOLS_READ,
                    Routes.ROUTE_TOOLS_EDIT,
                    DrawerState.Closed,
                    tool.id.toString()
                )
            );
        } else {
            history.push(
                buildSubPageRoute(
                    Routes.ROUTE_TOOLS_SUBPAGE,
                    Routes.ROUTE_TOOLS_SUBPAGE_NEW,
                    Routes.ROUTE_TOOLS_SUBPAGE_EDIT,
                    DrawerState.Closed,
                    tool.id.toString(),
                    'attachments'
                )
            );
        }
    };

    private handleAttachmentEdit = (id: number) => {
        const { tool } = this.state;

        if (!id) return;

        const attachments = tool?.attachments || [];
        const attachment = attachments.find((a: AttachmentVm): boolean => a.id === id);

        if (!attachment) return;

        this.handleDrawerChange(DrawerState.Edit, attachment);
    };

    private handleAttachmentDeleted = (id: number) => {
        const { tool } = this.state;

        if (!tool) return;

        this.setState({
            tool: ToolDetailVm.fromJS({
                ...tool,
                attachments: tool.attachments?.filter((i) => i.id !== id),
            }),
        });
    };

    private handleDrawerChange = (drawerState: DrawerState, selectedEntity?: AttachmentVm) => {
        const { history } = this.props;
        const { tool, activeTab } = this.state;

        if (!tool) return;

        const isEdit = drawerState === DrawerState.Edit;

        this.setState({
            drawerState,
            selectedAttachment:
                drawerState === DrawerState.Create
                    ? undefined
                    : isEdit
                      ? selectedEntity
                      : undefined,
        });

        if (activeTab === 'details') {
            history.push(
                buildRoute(
                    Routes.ROUTE_TOOLS,
                    Routes.ROUTE_TOOLS_NEW,
                    Routes.ROUTE_TOOLS_READ,
                    Routes.ROUTE_TOOLS_EDIT,
                    drawerState,
                    tool.id.toString()
                )
            );
        } else {
            history.push(
                buildSubPageRoute(
                    Routes.ROUTE_TOOLS_SUBPAGE,
                    Routes.ROUTE_TOOLS_SUBPAGE_NEW,
                    Routes.ROUTE_TOOLS_SUBPAGE_EDIT,
                    drawerState,
                    tool.id.toString(),
                    'attachments',
                    selectedEntity?.id.toString()
                )
            );
        }
    };

    private getDrawerTitle = (): string => {
        const { activeTab, selectedAttachment } = this.state;

        if (activeTab === 'details') {
            return translations.tools.editToolEquipment;
        }

        if (activeTab === 'attachments') {
            return selectedAttachment ? translations.attachments.edit : translations.attachments.add;
        }

        return '';
    };

    private handleAttachmentSaved = (result: AttachmentVm) => {
        const { tool } = this.state;

        if (!tool) return;

        const index = tool.attachments?.findIndex(
            (a: AttachmentVm): boolean => a.id === result.id
        );

        if (index !== -1) {
            this.setState({
                tool: ToolDetailVm.fromJS({
                    ...tool,
                    attachments: [
                        ...tool.attachments!.slice(0, index),
                        result,
                        ...tool.attachments!.slice(index! + 1),
                    ],
                }),
            });
        } else {
            this.setState({
                tool: ToolDetailVm.fromJS({
                    ...tool,
                    attachments: [...tool.attachments!, result],
                }),
            });
        }

        this.handleDrawerChange(DrawerState.Closed);
    };

    private closeDrawer = (): void => {
        const { activeTab } = this.state;
        if (activeTab === 'details') {
            this.handleDrawerMode(DrawerState.Closed);
        } else {
            this.handleDrawerChange(DrawerState.Closed);
        }
    };

    public render(): React.ReactElement {
        const {
            tool,
            drawerState,
            selectedAttachment,
            activeTab,
            allowUpdate,
            allowDelete,
            allowCreateAttachment,
        } = this.state;
        const { userProfile } = this.props;

        const documentAttachments =
            tool?.attachments?.filter((a) => !a.isMultimedia) ?? [];

        return (
            <>
                <PageHeader title={tool?.name || ''} />

                <Tabs
                    style={{ width: '100%' }}
                    activeKey={activeTab}
                    onTabClick={(key: string) => this.handleTabChange(key)}
                    destroyInactiveTabPane={true}
                >
                    <TabPane tab={translations.general.details} key="details">
                        {tool && (
                            <PageHeader
                                extra={
                                    <Space style={{ float: 'right' }}>
                                        {allowUpdate && (
                                            <Button
                                                icon={<EditOutlined />}
                                                onClick={() => this.handleDrawerMode(DrawerState.Edit)}
                                            >
                                                {translations.general.edit}
                                            </Button>
                                        )}
                                        {allowDelete && (
                                            <Button
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() =>
                                                    showConfirm(
                                                        () =>
                                                            remove(
                                                                new ToolsClient(),
                                                                translations.tools.deleteSuccess,
                                                                this.handleDelete,
                                                                tool.id
                                                            ),
                                                        translations.tools.deleteToolConfirm,
                                                        translations.general.delete,
                                                        true
                                                    )
                                                }
                                            >
                                                {translations.general.delete}
                                            </Button>
                                        )}
                                    </Space>
                                }
                            />
                        )}

                        {tool && (
                            <ToolDetails
                                tool={tool}
                                canEditMultimedia={allowUpdate}
                                onToolUpdated={(t) => this.setState({ tool: t })}
                            />
                        )}
                    </TabPane>

                    <TabPane
                        tab={translations.attachments.attachments}
                        key="attachments"
                    >
                        <PageHeader
                            title=""
                            extra={
                                allowCreateAttachment
                                    ? [
                                          <Button
                                              key="1"
                                              type="primary"
                                              style={{
                                                  zIndex: 10,
                                                  float: 'right',
                                              }}
                                              onClick={() =>
                                                  this.handleDrawerChange(DrawerState.Create)
                                              }
                                              icon={<PlusOutlined />}
                                          >
                                              {translations.attachments.add}
                                          </Button>,
                                      ]
                                    : []
                            }
                        />
                        {tool && (
                            <AttachmentTable
                                userProfile={userProfile}
                                attachments={documentAttachments}
                                onUpdate={this.handleAttachmentEdit}
                                onDelete={(id: number) =>
                                    remove(
                                        new AttachmentsClient(),
                                        translations.attachments.deleteSuccess,
                                        () => this.handleAttachmentDeleted(id),
                                        id
                                    )
                                }
                            />
                        )}
                    </TabPane>
                </Tabs>

                <Drawer
                    title={this.getDrawerTitle()}
                    open={!!drawerState}
                    onClose={this.closeDrawer}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    {activeTab === 'details' && tool && (
                        <ToolForm
                            {...this.props}
                            tool={tool}
                            onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                            onSuccess={this.handleSuccess}
                        />
                    )}
                    {activeTab === 'attachments' && (
                        <AttachmentForm
                            attachment={selectedAttachment}
                            tool={tool}
                            onSuccess={this.handleAttachmentSaved}
                            onClose={() => this.handleDrawerChange(DrawerState.Closed)}
                        />
                    )}
                </Drawer>
            </>
        );
    }
}

export default withRouter(ToolPage);
